// Browser system-notification helper.
// Used as a fallback for incoming calls when audio autoplay is blocked
// (browser refuses ringtone playback until the user has interacted with
// the page). The OS notification can play its own sound and works even
// when the tab is in the background.

const NotificationService = {
  isSupported() {
    return typeof window !== "undefined" && "Notification" in window;
  },

  permission() {
    return this.isSupported() ? Notification.permission : "denied";
  },

  // Must be called from a user gesture (login click, etc.) for
  // browsers that throttle requestPermission outside one.
  async ensurePermission() {
    if (!this.isSupported()) return false;
    if (Notification.permission === "granted") return true;
    if (Notification.permission === "denied") return false;
    try {
      const result = await Notification.requestPermission();
      return result === "granted";
    } catch (e) {
      console.warn("Notification.requestPermission failed", e);
      return false;
    }
  },

  show({ title, body, tag, requireInteraction = false, onClick } = {}) {
    if (!this.isSupported() || Notification.permission !== "granted") return null;
    try {
      const notification = new Notification(title, {
        body,
        tag,
        renotify: !!tag,
        requireInteraction,
        silent: false,
      });
      notification.onclick = () => {
        try {
          window.focus();
        } catch (e) {
          /* noop */
        }
        if (typeof onClick === "function") onClick();
        notification.close();
      };
      return notification;
    } catch (e) {
      console.warn("NotificationService.show failed", e);
      return null;
    }
  },

  notifyIncomingCall({ caller, fallbackTitle = "Incoming Call" } = {}) {
    const title = caller ? `Incoming call — ${caller}` : fallbackTitle;
    const body = caller ? `From ${caller}` : "You have an incoming call";
    return this.show({
      title,
      body,
      tag: "incoming-call",
      requireInteraction: true,
    });
  },
};

export default NotificationService;
